import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useWindowSize } from '@/utils/WindowSizeProvider';

import SearchResults from '../searchResults/SearchResults';
import Pagination from '../../containers/pagination/Pagination';
import { InvalidFiltersWarning } from '../../components/invalidFiltersWarning/InvalidFiltersWarning';
import { TopBarMap } from '../../components/topBarMap/TopBarMap';
import type { Filters, MapBounds, MapConfig, MapState, SearchConfig } from '@/interfaces';
import { TopBarMobile } from '../../containers/topBarMobile/TopBarMobile';
import { SearchResultsSortDropdown } from '../searchResultsSortDropdown/SearchResultsSortDropdown';
import { SearchResultsFilters } from '../searchResultsFilters/SearchResultsFilters';
import { EditPanelExpandCallback } from '@/components/editPanel/EditPanel';
import SearchResultsSearchFormPreview from '../searchResultsSearchForm/SearchResultsSearchFormPreview';
import { SearchResultsSearchForm } from '../searchResultsSearchForm/SearchResultsSearchForm';
import { scrollToElementById, scrollToTop } from '@/utils/scroll';
import { SearchResultsMap } from '../searchResultMap/SearchResultsMap';
import { SelectedFilters } from '../searchResultsFilters/SelectedFilters';
import { useIsomorphicLayoutEffect } from '@/utils/useIsomorphicLayoutEffect';

interface Props {
    filters: Filters;
    filterErrors: readonly string[];
    numberOfHotels: number;
    filtersOpen: boolean;
    setFiltersOpen: (open: boolean) => void;
    mapState: MapState;
    searchConfig: SearchConfig;
    setMapState: (config: MapConfig) => void;
    setMapBounds: (mapBounds: MapBounds) => void;
    triggerFetch: (resetMap: boolean) => void;
}

export const SearchResultsMapLayout = ({
    filters,
    filterErrors,
    numberOfHotels,
    filtersOpen,
    setFiltersOpen,
    mapState,
    searchConfig,
    setMapState,
    setMapBounds,
    triggerFetch,
    children,
}: PropsWithChildren<Props>) => {
    const { isMobile } = useWindowSize();

    const [openEditPanel, setOpenEditPanel] = useState(!filters.checkInDate || !filters.checkOutDate);
    const focusRef = useRef<HTMLInputElement>();
    const focusOnRender = useRef(false);

    const openEditPanelWithFocus = () => {
        if (!openEditPanel) {
            focusOnRender.current = true;
            setOpenEditPanel(true);
        } else {
            focusRef.current.focus({ preventScroll: true });
        }
        scrollToElementById('editPanel');
    };

    useIsomorphicLayoutEffect(() => {
        if (focusOnRender.current) {
            focusRef.current.focus({ preventScroll: true });
            focusOnRender.current = false;
        }
    });

    useEffect(() => {
        if (!isMobile) {
            scrollToTop();
        }
    }, []);

    return (
        <div>
            <TopBarMobile
                numberOfHotels={numberOfHotels}
                mapOpen={mapState.open}
                setMapState={setMapState}
                filtersOpen={filtersOpen}
                setFiltersOpen={setFiltersOpen}
                filters={filters}
                searchConfig={searchConfig}
            />
            <div>
                <div className="map-result-list u-ph8 u-hide-lt768">
                    <TopBarMap
                        numberOfHotels={numberOfHotels}
                        setMapState={setMapState}
                        filtersOpen={filtersOpen}
                        setFiltersOpen={setFiltersOpen}
                        filters={filters}
                        searchConfig={searchConfig}
                    />
                    <EditPanelExpandCallback
                        expanded={openEditPanel}
                        setExpanded={setOpenEditPanel}
                        preview={<SearchResultsSearchFormPreview />}
                        className="u-mv16 u-mh8">
                        <SearchResultsSearchForm defaultErrorOnMissingDates checkinDateRef={focusRef} />
                    </EditPanelExpandCallback>
                    <SelectedFilters openFilters={() => setFiltersOpen(true)} className="map-view" />
                    {filterErrors.length > 0 && numberOfHotels > 0 && <InvalidFiltersWarning filterErrors={filterErrors} />}
                    <SearchResults className="map-view" openEditPanelWithFocus={openEditPanelWithFocus} />
                    <Pagination />
                </div>
                <SearchResultsMap
                    filters={filters}
                    mapState={mapState}
                    numberOfHotels={numberOfHotels}
                    setMapState={setMapState}
                    setMapBounds={setMapBounds}
                    triggerFetch={triggerFetch}
                    searchConfig={searchConfig}
                />
                <SearchResultsFilters filtersOpen={filtersOpen} className="map-view u-pt16--gt768">
                    <button
                        type="button"
                        data-testid="closeButton"
                        className="filters-close-button button--close u-hide-lt768"
                        aria-label="Lukk filtere"
                        onClick={() => setFiltersOpen(false)}
                    />
                    <div className="u-pv16 u-ph8">
                        <SearchResultsSortDropdown id="sortDropdownMap" labelClassName="u-t4 u-mb16" />
                    </div>
                    {children}
                </SearchResultsFilters>
            </div>
        </div>
    );
};
